import React from 'react';
import { CertificateTemplate } from '../../../apis/api-client';
import { Box, Button, IconButton, Tooltip, Typography } from '../../ui';
import { AddIcon, DeleteIcon, VisibilityIcon, VisibilityOffIcon } from '../../icons';
import { isCustomText, labelForField, SYSTEM_FIELD_LABELS, SYSTEM_FIELD_TYPES } from './constants';

/**
 * Text-field selector for the certificate editor. Renders a chip per
 * system field (with a show/hide eye toggle) plus a chip per custom-text
 * field (with a delete button), and an "Add custom text" action. Clicking
 * a chip selects that field for editing in the controls panel below.
 */
export default function CertificateFieldChips({
  template,
  selectedField,
  enabledFields,
  onSelectField,
  onToggleField,
  onAddCustomText,
  onDeleteCustomText,
}: {
  template: CertificateTemplate;
  selectedField: string;
  enabledFields: Record<string, boolean>;
  onSelectField: (fieldType: string) => void;
  onToggleField: (fieldType: string, enabled: boolean) => void;
  onAddCustomText: () => void;
  onDeleteCustomText: (fieldType: string) => void;
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          TEXT FIELDS
        </Typography>
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={onAddCustomText}
          sx={{ textTransform: 'none', py: 0.25, fontSize: '0.75rem' }}
        >
          Add custom text
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {/* System fields */}
        {SYSTEM_FIELD_TYPES.map((fieldType) => {
          const isSelected = selectedField === fieldType;
          const isEnabled = enabledFields[fieldType];
          return (
            <Box
              key={fieldType}
              onClick={() => onSelectField(fieldType)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                pl: 1.25,
                pr: 0.5,
                py: 0.25,
                borderRadius: 999,
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                bgcolor: isSelected ? 'rgba(var(--brand-rgb), 0.12)' : 'transparent',
                color: isEnabled ? 'text.primary' : 'text.disabled',
                cursor: 'pointer',
                transition: 'all 120ms ease',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Typography variant="caption" fontWeight={isSelected ? 700 : 500}>
                {SYSTEM_FIELD_LABELS[fieldType]}
              </Typography>
              <Tooltip title={isEnabled ? 'Hide on certificate' : 'Show on certificate'}>
                <IconButton
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onToggleField(fieldType, !isEnabled);
                  }}
                  sx={{ p: 0.25 }}
                >
                  {isEnabled ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <VisibilityOffIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}

        {/* Custom-text fields (only visible when present) */}
        {template.textFields
          .filter((f) => isCustomText(f.fieldType))
          .map((field) => {
            const isSelected = selectedField === field.fieldType;
            return (
              <Box
                key={field.fieldType}
                onClick={() => onSelectField(field.fieldType)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: 1.25,
                  pr: 0.5,
                  py: 0.25,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'rgba(var(--brand-rgb), 0.12)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 120ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={isSelected ? 700 : 500}
                  fontStyle="italic"
                  sx={{ color: 'text.primary' }}
                >
                  {labelForField(field)}
                </Typography>
                <Tooltip title="Delete this text">
                  <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onDeleteCustomText(field.fieldType);
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
}
